'use client';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthBoot } from '../../context/AuthBootContext';
import { openLink } from '@/lib/utils/url';
import { DEFAULT_TEXT_COLOR } from '../../constants';
import { getResolvedFooter } from '@/lib/utils/storeSettings';

interface ISimpleTermsProps {
    textColor?: string;
    /** Set when the design links to its own legal pages instead of the publisher's. */
    privacyPolicyUrl?: string;
    termsAndConditionsUrl?: string;
}

export const SimpleTerms = ({
    textColor,
    privacyPolicyUrl,
    termsAndConditionsUrl,
}: ISimpleTermsProps): React.ReactElement => {
    const { t } = useTranslation();
    const { authBoot } = useAuthBoot();

    // Per-field merge to mirror store-frontend's `resolveFooter` — object
    // fallback would lose privacy/terms URLs whenever a partial override is set.
    const footerSettings = useMemo(
        () => getResolvedFooter(authBoot.storeTheme),
        [authBoot.storeTheme],
    );
    const resolvedTextColor = textColor || authBoot.storeTheme.login?.textColor || DEFAULT_TEXT_COLOR;
    const openInNewTab = footerSettings?.openLinksInNewTab || false;

    const handleLinkClick = useCallback((url: string | undefined): void => {
        if (!url) return;
        openLink(url, openInNewTab);
    }, [openInNewTab]);

    return (
        <span
            className="terms-wrapper"
            style={{ color: resolvedTextColor }}
            data-testid="login-simple-terms"
        >
            {t('login.byLoggingIn')}{' '}
            <span
                className="login-link cursor-pointer underline"
                onClick={() => handleLinkClick(privacyPolicyUrl ?? footerSettings?.privacyPolicyUrl)}
            >
                {t('login.privacyPolicy')}
            </span>{' '}
            {t('login.and')}{' '}
            <span
                className="login-link cursor-pointer underline"
                onClick={() =>
                    handleLinkClick(termsAndConditionsUrl ?? footerSettings?.termsAndConditionsUrl)
                }
            >
                {t('login.termsAndConditions')}
            </span>
        </span>
    );
};
